import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { SvgIcon } from '@progress/kendo-react-common';
import { bellIcon, gearIcon } from '@progress/kendo-svg-icons';
import { ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { useLocalization } from '@progress/kendo-react-intl';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import useIdentityStore from '../../../../Zustand/identityStore';
import { GetBulkFlowIcon, GetEndpointIcon, GetEndpointMP2Icon, GetGatewayHMEIcon, GetGreasePumpGlyphIcon } from '../../../../Utilities/CustomIcons';
import { DeviceType } from '../../../../Enums/DeviceType';
import { getCriticalityIcon } from '../../../Shared/Utilities/criticalityIcon';
import { enMessages } from '../../../../messages/en-US';
import SelectedAssetMetricsReadDto from '../../../../Models/Dashboard/SelectedAssetMetricsReadDto';
import notesApiService from '../Utilities/NotesApiService';
import type { Note, EntityType } from '../Components/Notes/EntityNotesPanel';
import { useNoteCategories } from '../Components/Notes/noteHooks';
import NotesTimelineView, { TimelineViewNote, defaultNotesDateRange } from '../Components/Notes/NotesTimelineView';
import { ENTITY_TYPE_OPTIONS } from '../Components/Notes/entityTypeOptions';

export interface AssetNotesTabRef {
  refresh: () => Promise<void>;
}

interface Props {
  asset: SelectedAssetMetricsReadDto;
}

const AssetNotesTab = forwardRef<AssetNotesTabRef, Props>(({ asset }, ref) => {
  const localizationService = useLocalization();
  const currentUserId = useIdentityStore((x) => x.user?.profile?.sub) || '';
  const queryClient = useQueryClient();
  const { categoryLabel } = useNoteCategories();

  // `${entityType}:${entityId}` → display name. Drives the entity context shown on each row.
  const entityNameMap = useMemo(() => {
    const map = new Map<string, string>();
    map.set(`Asset:${asset.id}`, asset.name);
    asset.assetConfigurationDetails?.endpoints?.forEach((e) => map.set(`Endpoint:${e.id}`, e.name));
    asset.assetConfigurationDetails?.gateways?.forEach((g) =>
      map.set(`Device:${g.id}`, g.name || g.serialNumber || `Gateway ${g.id}`),
    );
    asset.assetConfigurationDetails?.greasePumps?.forEach((p) =>
      map.set(`GreasePump:${p.id}`, p.name || p.serialNumber || `Grease Pump ${p.id}`),
    );
    return map;
  }, [asset]);

  // Endpoint-id → device-level entityType, so the row icon matches the EndpointTableComponent.
  const endpointDeviceTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    asset.assetConfigurationDetails?.endpoints?.forEach((e) => map.set(String(e.id), e.entityType));
    return map;
  }, [asset]);

  // `${entityType}:${entityId}` → serialNumber (shown after the entity name).
  const entitySerialMap = useMemo(() => {
    const map = new Map<string, string>();
    asset.assetConfigurationDetails?.endpoints?.forEach((e) => {
      if (e.serialNumber) map.set(`Endpoint:${e.id}`, e.serialNumber);
    });
    asset.assetConfigurationDetails?.gateways?.forEach((g) => {
      if (g.serialNumber) map.set(`Device:${g.id}`, g.serialNumber);
    });
    asset.assetConfigurationDetails?.greasePumps?.forEach((p) => {
      if (p.serialNumber) map.set(`GreasePump:${p.id}`, p.serialNumber);
    });
    return map;
  }, [asset]);

  // Date range owns the server query (refetches on change); the view renders the pickers.
  const [dateRange, setDateRange] = useState(defaultNotesDateRange);
  const startISO = dateRange.start ? dateRange.start.toISOString() : undefined;
  const endISO = dateRange.end ? dateRange.end.toISOString() : undefined;
  const allNotesQuery = useQuery(
    ['assetAllNotes', asset.id, startISO, endISO],
    () => notesApiService.getAllAssetNotes(asset.id, startISO, endISO),
    { enabled: !!asset.id, staleTime: 30 * 1000, keepPreviousData: true },
  );
  const allNotes = useMemo<Note[]>(
    () => (allNotesQuery.data ?? []).slice().sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
    [allNotesQuery.data],
  );

  const invalidateNotes = () => queryClient.invalidateQueries(['assetAllNotes', asset.id]);
  const editNoteMutation = useMutation(
    ({ noteId, input }: { noteId: string; input: { description: string; categories: Note['categories']; isInternalOnly?: boolean } }) =>
      notesApiService.updateNote(noteId, { comment: input.description, categories: input.categories, isInternalOnly: input.isInternalOnly }),
    { onSuccess: invalidateNotes },
  );
  const deleteNoteMutation = useMutation(
    (noteId: string) => notesApiService.deleteNote(noteId),
    { onSuccess: invalidateNotes },
  );

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      await allNotesQuery.refetch();
    },
  }));

  const labelFor = (entityType: string) => ENTITY_TYPE_OPTIONS.find((t) => t.value === entityType)?.text ?? entityType;

  // Treat any note whose entityId is the asset itself as `Asset` (keeps the entity filter consistent).
  const normalizeEntityType = (n: Note): EntityType => (n.entityId === asset.id ? 'Asset' : n.entityType);

  /** Per-entityType row icon. Endpoints refine to their device-level variant (MP2 / BF / plain).
   *  `Alert` uses the criticality chevron (matching the drawer header, bell fallback); `AlertNotification` a bell. */
  const renderEntityIcon = (entityType: EntityType, entityId: string, criticality?: string) => {
    if (entityType === 'Endpoint' || entityType === 'Point') {
      const deviceType = endpointDeviceTypeMap.get(entityId);
      if (deviceType === DeviceType.EndpointMP2) return GetEndpointMP2Icon(18, 18);
      if (deviceType === DeviceType.EndpointBF) return GetBulkFlowIcon(18, 18);
      return GetEndpointIcon(18, 18);
    }
    if (entityType === 'Device') return GetGatewayHMEIcon(18, 18);
    if (entityType === 'GreasePump') return GetGreasePumpGlyphIcon(18, 18);
    if (entityType === 'Alert') return getCriticalityIcon(criticality) ?? <SvgIcon icon={bellIcon} size="medium" />;
    if (entityType === 'AlertNotification') return <SvgIcon icon={bellIcon} size="medium" />;
    return <SvgIcon icon={gearIcon} size="medium" />;
  };

  const viewNotes = useMemo<TimelineViewNote[]>(
    () =>
      allNotes.map((n) => {
        const entityName =
          n.entityId === asset.id
            ? asset.name
            : entityNameMap.get(`${n.entityType}:${n.entityId}`) ?? n.entityName ?? '';
        const serialNumber = entitySerialMap.get(`${n.entityType}:${n.entityId}`) ?? '';
        const typeLabel = labelFor(normalizeEntityType(n));
        const categoryLabels = n.categories.map(categoryLabel).join(' ');
        return {
          ...n,
          entityType: normalizeEntityType(n),
          header: (
            <div className="flex min-w-0 items-center gap-2">
              <span className="inline-flex shrink-0 items-center justify-center" title={typeLabel}>
                {renderEntityIcon(n.entityType, n.entityId, n.entityMetadata?.criticality)}
              </span>
              <span className="truncate text-sm font-semibold">
                {entityName || n.entityId}
                {serialNumber && <span className="ml-1 font-normal opacity-70">({serialNumber})</span>}
              </span>
            </div>
          ),
          searchText: `${n.description} ${n.createdBy} ${entityName} ${serialNumber} ${typeLabel} ${categoryLabels}`.toLowerCase(),
          exportRow: {
            createdAt: moment(n.createdAt).format('DD/MM/YYYY HH:mm'),
            entityTypeLabel: labelFor(n.entityType),
            entityName: entityName || n.entityId,
            serialNumber,
            categories: n.categories.map(categoryLabel).join(', '),
            description: n.description,
            createdBy: n.createdBy,
          },
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allNotes, asset.id, asset.name, entityNameMap, entitySerialMap, endpointDeviceTypeMap],
  );

  const exportColumns = (
    <>
      <ExcelExportColumn field="createdAt" title={localizationService.toLanguageString('custom.date', enMessages.custom.date)} width={140} />
      <ExcelExportColumn field="entityTypeLabel" title="Entity Type" width={120} />
      <ExcelExportColumn field="entityName" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} width={200} />
      <ExcelExportColumn field="serialNumber" title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)} width={150} />
      <ExcelExportColumn field="categories" title={localizationService.toLanguageString('custom.category', enMessages.custom.category)} width={200} />
      <ExcelExportColumn field="description" title={localizationService.toLanguageString('custom.description', enMessages.custom.description)} width={400} />
      <ExcelExportColumn field="createdBy" title={localizationService.toLanguageString('custom.createdBy', enMessages.custom.createdBy)} width={160} />
    </>
  );

  return (
    <NotesTimelineView
      notes={viewNotes}
      entityTypeOptions={ENTITY_TYPE_OPTIONS}
      exportColumns={exportColumns}
      exportFileName={`${asset.name || 'Asset'} - Notes.xlsx`}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      isLoading={allNotesQuery.isLoading}
      isError={allNotesQuery.isError}
      errorMessage={(allNotesQuery.error as Error)?.message}
      isFetching={allNotesQuery.isFetching}
      onRefresh={() => allNotesQuery.refetch()}
      onSave={(noteId, input) => editNoteMutation.mutate({ noteId, input })}
      onDelete={(noteId) => deleteNoteMutation.mutate(noteId)}
      currentUserId={currentUserId}
    />
  );
});

AssetNotesTab.displayName = 'AssetNotesTab';

export default AssetNotesTab;
